import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom, merge } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Activity, ActivityCriteria, ActivityType, CallInterface, TransferActivity } from 'voxr-shared';
import { SocketService } from './socket.service';
import { UserService } from './user.service';
import { WorkspaceService } from './workspace.service';
import { SoftphoneBrowserSessionService } from './softphone-browser-session.service';

export interface AgentTask {
  activityId: string;
  isTestFixture: boolean;
  subjectAgentId: string | null;
  participationId: string;
  type: 'transfer' | 'appointment';
  status?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  campaign: { id: string; name: string };
  agent: { id: string; displayName: string; picture?: string | null; availabilityEligible?: boolean } | null;
  contactId: string | null;
  contact: { name: string | null; phone: string | null; state?: string | null } | null;
  disposition: string | null;
  note: string | null;
  outcomeResolution: 'resolved' | 'none' | 'unresolved';
  isWinningParticipation: boolean;
  handledByAnotherAgent: boolean;
  repairStatus?: { downstream: Partial<Record<'callOutcome' | 'crm' | 'campaignCheck' | 'transferUsage' | 'taskRefresh', 'completed' | 'failed' | 'skipped'>> };
  recording: { available: boolean; reason: string | null; agent: { playbackUrl: string; downloadUrl: string } | null; ai: { playbackUrl: string; downloadUrl: string } | null };
  capabilities: { viewDetails: boolean; viewHistory: boolean; viewFullHistory?: boolean; changeDisposition: boolean; editOrdinaryNote: boolean; createFeedback: boolean; callback: boolean; submitDispute: boolean; reviewDispute: boolean; export: boolean };
  dispositionChoices?: { key: string; label: string }[];
  callReview?: { outcome: string | null; summary: string | null; transcript: string | null; summaryTruncated?: boolean; durationSeconds: number | null } | null;
  transferReview?: { status: string | null; callStatus?: string | null; durationSeconds: number | null; ringSeconds: number | null; completedAt: string | null } | null;
  independentActions?: { callId: string | null; fromNumber: string | null; toNumber?: string | null;
    transfer: { sid: string; integrationId: string } | null } | null;
  exportData?: { outcome: string | null; summary: string | null; durationSeconds: number | null };
  dispute?: { status: 'pending' | 'approved' | 'rejected' | null; reason: string | null; reviewNote?: string | null; submittedAt?: string | null };
}
export interface AgentTaskSearch { campaignIds?: string[]; agentIds?: string[]; kinds?: ('transfer' | 'appointment')[]; from?: string; to?: string; keywords?: string; cursor?: string; limit?: number; purpose?: 'view' | 'export'; disputeStatus?: 'all' | 'pending' | 'approved' | 'rejected'; subjectAgentId?: string | null; participationId?: string }
export interface AgentTaskPage { items: AgentTask[]; hasMore: boolean; nextCursor: string | null; emptyCoachScope?: boolean }
export interface AgentTaskHistoryNote { type: 'note'; activityId: string; subjectAgentId: string; participationId: 'note'; agent: { id: string; displayName: string }; campaign: { id: string; name: string }; contactId: string; createdAt: string; note: string }
export interface AgentTaskHistoryPage { items: (AgentTask | AgentTaskHistoryNote)[]; hasMore: boolean; nextCursor: string | null }
export interface AgentTaskOptionPage { items: { id: string; label: string }[]; hasMore: boolean; nextCursor: string | null; canFilterAgents?: boolean }
export interface AgentTaskCampaignContext { disputesEnabled: boolean; creditEligible: boolean; canReviewDisputes: boolean; shareMessage: string | null }
export interface AgentTaskFeedback { id: string; activityId: string; subjectAgentId: string | null; participationId: string; authorUserId: string; body: string; createdAt: string; updatedAt: string; canEdit: boolean }
export interface AgentTaskFeedbackPage { items: AgentTaskFeedback[]; hasMore: boolean; nextCursor: string | null }

export interface SubmitTransferCreditDisputeRequest {
  activityId: string;
  reason: string;
}

export interface ReviewTransferCreditDisputeRequest {
  activityId: string;
  accepted: boolean;
  reviewNote?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ActivityService {
  private http = inject(HttpClient);
  private socketService = inject(SocketService);
  private userService = inject(UserService);
  private workspaceService = inject(WorkspaceService);
  private browserSession = inject(SoftphoneBrowserSessionService);
  private activeCoachTransferSubject = new BehaviorSubject<TransferActivity | undefined>(undefined);
  private subscribedWorkspaceId?: string;

  constructor() {
    combineLatest([this.socketService.connected$, this.userService.user$, this.workspaceService.workspace$])    
      .pipe(filter(([isConnected, user, workspace]) => isConnected && !!user && !!workspace))
      .subscribe(([, , workspace]) => {
        void this.subscribeWorkspace(workspace!._id!.toString());
      });
  }

  readonly transferStarted$ = this.socketService.onEvent$<TransferActivity>('transfer-started').pipe(
    filter(transfer => transfer.workspaceId?.toString() === this.workspaceService.workspaceId)
  );
  readonly transferEnded$ = this.socketService.onEvent$<TransferActivity>('transfer-ended').pipe(
    filter(transfer => transfer.workspaceId?.toString() === this.workspaceService.workspaceId)
  );
  readonly coachingLiveRefresh$ = merge(
    this.socketService.onEvent$<{ campaignId: string }>('coaching-live-refresh'),
    this.socketService.onEvent$<{ campaignId: string }>('coaching-conference-refresh'),
  );
  readonly activeCoachTransfer$ = this.activeCoachTransferSubject.asObservable();

  list(criteria: ActivityCriteria) {
    return firstValueFrom(this.http.post<Activity[]>(`${environment.app.serviceUrl}/activity/list`, criteria));
  }

  getById(id: string) {
    return firstValueFrom(this.http.get<Activity>(`${environment.app.serviceUrl}/activity/${id}`));
  }

  getByCallId(callId: string, type?: ActivityType) {
    return this.list({ callId, type });
  }

  getByContactId(contactId: string, type?: ActivityType) {
    return this.list({ contactId, type });
  }

  getByCampaignId(campaignId: string, type?: ActivityType) {
    return this.list({ campaignId, type });
  }

  setDisposition(activityId: string, disposition: string | null, note?: string) {
    return firstValueFrom(this.http.post<void>(`${environment.app.serviceUrl}/activity/disposition`, { activityId, disposition, note }));
  }

  getHistory(campaignId: string, contactId: string) {
    return firstValueFrom(this.http.get<{ call: CallInterface; activity?: Activity }[]>(`${environment.app.serviceUrl}/activity/history/${campaignId}/${contactId}`));
  }

  searchAgentTasks(query: AgentTaskSearch) {
    return firstValueFrom(this.http.post<AgentTaskPage>(`${environment.app.serviceUrl}/workspace/agent-tasks/search`, query));
  }

  agentTaskOptions(kind: 'campaign' | 'agent', q = '', cursor?: string, ids?: string[]) {
    return firstValueFrom(this.http.post<AgentTaskOptionPage>(`${environment.app.serviceUrl}/workspace/agent-tasks/options`, { kind, ...(ids ? { ids } : { q, ...(cursor ? { cursor } : {}) }) }));
  }

  getAgentTask(task: AgentTask) {
    return firstValueFrom(this.http.get<AgentTask>(`${environment.app.serviceUrl}/workspace/agent-tasks/${task.activityId}`, {
      params: { ...(task.subjectAgentId ? { subjectAgentId: task.subjectAgentId } : {}), participationId: task.participationId }
    }));
  }

  getAgentTaskCampaignContext(campaignId: string) {
    return firstValueFrom(this.http.get<AgentTaskCampaignContext>(
      `${environment.app.serviceUrl}/workspace/agent-tasks/campaign-context/${campaignId}`));
  }

  agentTaskHistory(task: AgentTask, cursor?: string) {
    return firstValueFrom(this.http.post<AgentTaskHistoryPage>(`${environment.app.serviceUrl}/workspace/agent-tasks/${task.activityId}/history`, {
      subjectAgentId: task.subjectAgentId, participationId: task.participationId, limit: 25,
      ...(cursor ? { cursor } : {})
    }));
  }

  changeAgentTaskDisposition(task: AgentTask, disposition: string | null, note?: string) {
    return firstValueFrom(this.http.patch<AgentTask>(`${environment.app.serviceUrl}/workspace/agent-tasks/${task.activityId}/disposition`, {
      subjectAgentId: task.subjectAgentId, participationId: task.participationId, disposition,
      ...(note === undefined ? {} : { note }),
      expectedUpdatedAt: task.updatedAt, expectedVersion: task.version,
    }));
  }

  changeAgentTaskNote(task: AgentTask, note: string) {
    return firstValueFrom(this.http.patch<{ note: string | null; updatedAt: string; version: number }>(
      `${environment.app.serviceUrl}/workspace/agent-tasks/${task.activityId}/note`, {
      subjectAgentId: task.subjectAgentId, participationId: task.participationId, note,
      expectedUpdatedAt: task.updatedAt, expectedVersion: task.version,
    }));
  }

  listAgentTaskFeedback(task: AgentTask, cursor?: string) {
    return firstValueFrom(this.http.get<AgentTaskFeedbackPage>(`${environment.app.serviceUrl}/workspace/agent-tasks/${task.activityId}/feedback`, {
      params: { ...(task.subjectAgentId ? { subjectAgentId: task.subjectAgentId } : {}),
        participationId: task.participationId, limit: 25, ...(cursor ? { cursor } : {}) }
    }));
  }

  createAgentTaskFeedback(task: AgentTask, body: string) {
    return firstValueFrom(this.http.post<AgentTaskFeedback>(
      `${environment.app.serviceUrl}/workspace/agent-tasks/${task.activityId}/feedback`,
      { subjectAgentId: task.subjectAgentId, participationId: task.participationId, body }
    ));
  }

  editAgentTaskFeedback(feedback: AgentTaskFeedback, body: string) {
    return firstValueFrom(this.http.patch<AgentTaskFeedback>(`${environment.app.serviceUrl}/workspace/agent-tasks/feedback/${feedback.id}`,
      { body }));
  }

  submitTransferCreditDispute(input: SubmitTransferCreditDisputeRequest) {
    return firstValueFrom(this.http.post<TransferActivity>(`${environment.app.serviceUrl}/activity/transfer/${input.activityId}/dispute`, {
      reason: input.reason
    }));
  }

  reviewTransferCreditDispute(input: ReviewTransferCreditDisputeRequest) {
    return firstValueFrom(this.http.post<TransferActivity>(`${environment.app.serviceUrl}/activity/transfer/${input.activityId}/dispute/review`, {
      accepted: input.accepted,
      reviewNote: input.reviewNote ?? null
    }));
  }

  async subscribeWorkspace(workspaceId: string) {
    this.subscribedWorkspaceId = await this.workspaceService.hasAdminAccess() ? workspaceId : undefined;

    if (this.socketService.connected$.value) {
      await this.emitWorkspaceSubscription();
      return;
    }

    try {
      await firstValueFrom(
        this.socketService.connected$.pipe(
          filter(isConnected => isConnected),
          take(1),
          timeout(15000),
        )
      );
    } catch {
      console.warn('ActivityService.subscribeWorkspace timed out waiting for socket connection');
    }
  }

  private async emitWorkspaceSubscription() {
    if (!this.subscribedWorkspaceId) return;
    this.socketService.emit('subscribe-workspace', { workspaceId: this.subscribedWorkspaceId });
    
  }

  getLiveTransfers() {
    return firstValueFrom(this.http.get<{ transfers: TransferActivity[] }>(`${environment.app.serviceUrl}/activity/live-transfers`));
  }

  async coachingJoin(activityId: string) {
    if (!await this.browserSession.ensureActiveForAction()) {
      throw new Error('Switch the soft phone to this browser before joining coaching.');
    }
    try {
      return await firstValueFrom(this.http.post<{ success: boolean; coachCallSid: string }>(
        `${environment.app.serviceUrl}/activity/coaching/join`,
        { activityId },
        { headers: this.browserSession.sessionHeaders() }
      ));
    } catch (error) {
      this.browserSession.handleSessionConflict(error);
      throw error;
    }
  }

  coachingLeave(activityId: string) {
    return firstValueFrom(this.http.post<{ success: boolean }>(`${environment.app.serviceUrl}/activity/coaching/leave`, { activityId }));
  }

  coachingMute(activityId: string, muted: boolean) {
    return firstValueFrom(this.http.post<{ success: boolean }>(`${environment.app.serviceUrl}/activity/coaching/mute`, { activityId, muted }));
  }

  coachingMode(activityId: string, mode: 'whisper' | 'barge') {
    return firstValueFrom(this.http.post<{ success: boolean }>(`${environment.app.serviceUrl}/activity/coaching/mode`, { activityId, mode }));
  }

  setActiveCoachTransfer(transfer?: TransferActivity) {
    this.activeCoachTransferSubject.next(transfer);
  }

  getActiveCoachTransfer() {
    return this.activeCoachTransferSubject.value;
  }

}
